// frontend/src/components/common/Modal.js
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';
import Button from './Button';

// Modals can nest (a photo viewer opened from the job-details modal). The open
// ones are tracked here, in the order they were first rendered open, so that
// only the topmost answers Escape and only the last to close unlocks scrolling.
let openSeq = 0;
const openModals = [];

const isTopmost = (seq) => openModals.every((m) => m.seq <= seq);

// Base Modal Component
export const Modal = ({
  isOpen = false,
  onClose,
  title = null,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  preventBodyScroll = true
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  // Parents render before children, so taking the number during render gives
  // an outer modal a lower number than one nested inside it, even when both
  // open in the same commit (effects would run child-first).
  const seqRef = useRef(0);
  if (!isOpen) seqRef.current = 0;
  else if (!seqRef.current) { openSeq += 1; seqRef.current = openSeq; }

  const sizeClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    xxl: 'max-w-7xl',
    full: 'max-w-full mx-4'
  };

  // Every consumer passes an inline arrow as onClose (`onClose={() =>
  // setShowCreateForm(false)}`), so its identity changes on every render of
  // the parent. Keeping it in a ref lets the Escape listener always call the
  // LATEST onClose without onClose being an effect dependency — which is what
  // previously made the effects below re-run on every keystroke.
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Focus capture/restore and the body-scroll lock are open/close concerns:
  // they must run exactly once per open, so this effect is keyed on `isOpen`
  // (plus preventBodyScroll) and nothing else. When onClose was a dependency
  // here, one keystroke in an overlay whose form state lives in the parent
  // re-ran the whole effect — yanking focus out of the field and into the
  // dialog panel, and thrashing body.style.overflow between hidden/unset.
  useEffect(() => {
    if (!isOpen) return undefined;

    // Store the currently focused element
    previousActiveElement.current = document.activeElement;

    // Focus the modal
    if (modalRef.current) {
      modalRef.current.focus();
    }

    const entry = { seq: seqRef.current, preventBodyScroll };
    openModals.push(entry);

    // Prevent body scroll
    if (preventBodyScroll) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      openModals.splice(openModals.indexOf(entry), 1);

      // Restore body scroll — only when no other open modal still holds the lock
      if (preventBodyScroll && !openModals.some((m) => m.preventBodyScroll)) {
        document.body.style.overflow = 'unset';
      }

      // Restore focus — but only if the element is still in the document.
      // Modal-opening loaders in JobManagement flip a shared isLoading that
      // makes DataTable swap the whole table out, so the trigger that opened
      // the modal is frequently DETACHED by the time we close. Calling
      // .focus() on an orphan node makes the browser drop focus to <body>,
      // which is worse than leaving focus where it is.
      const previous = previousActiveElement.current;
      if (previous && previous.isConnected) {
        previous.focus();
      }
    };
  }, [isOpen, preventBodyScroll]);

  // The Escape listener is a separate concern with separate dependencies, so
  // it binds/unbinds on its own without disturbing focus or the scroll lock.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isTopmost(seqRef.current)) {
        onCloseRef.current?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const modalContent = (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${overlayClassName}`}
      onClick={handleOverlayClick}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div data-testid="modal-backdrop" className="fixed inset-0 bg-ink/40 transition-opacity duration-overlay" />

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/* Modal panel */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`
            relative z-10 inline-block align-bottom bg-surface-card rounded-card border border-border-default text-left overflow-hidden shadow-overlay transform transition-all
            sm:my-8 sm:align-middle w-full ${sizeClasses[size]} ${className}
          `}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
              {title && (
                <h3 id={titleId} className="text-section-title font-display text-ink">
                  {title}
                </h3>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="min-w-touch min-h-touch flex items-center justify-center rounded-control text-ink-muted hover:text-ink hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 transition-colors duration-state"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-4 text-body text-ink-soft">
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Confirmation Modal
export const ConfirmModal = ({
  isOpen = false,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger', 'warning', 'info'
  loading = false
}) => {
  const variantConfig = {
    danger: {
      icon: AlertTriangle,
      iconColor: 'text-danger',
      bgColor: 'bg-danger-tint',
      buttonVariant: 'danger'
    },
    warning: {
      icon: AlertCircle,
      iconColor: 'text-warning',
      bgColor: 'bg-warning-tint',
      buttonVariant: 'primary'
    },
    info: {
      icon: Info,
      iconColor: 'text-info',
      bgColor: 'bg-info-tint',
      buttonVariant: 'primary'
    }
  };

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="sm:flex sm:items-start">
        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.bgColor} sm:mx-0 sm:h-10 sm:w-10`}>
          <Icon className={`h-6 w-6 ${config.iconColor}`} strokeWidth={1.5} />
        </div>
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3 className="text-section-title font-display text-ink">
            {title}
          </h3>
          <div className="mt-2">
            <p className="text-body text-ink-soft">
              {message}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          type="button"
          variant={config.buttonVariant}
          onClick={onConfirm}
          disabled={loading}
          loading={loading}
          className="w-full sm:ml-3 sm:w-auto"
        >
          {confirmText}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={loading}
          className="mt-3 w-full sm:mt-0 sm:w-auto"
        >
          {cancelText}
        </Button>
      </div>
    </Modal>
  );
};

// Alert Modal
export const AlertModal = ({
  isOpen = false,
  onClose,
  title = 'Alert',
  message,
  variant = 'info', // 'success', 'error', 'warning', 'info'
  buttonText = 'OK'
}) => {
  const variantConfig = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-success',
      bgColor: 'bg-success-tint'
    },
    error: {
      icon: AlertTriangle,
      iconColor: 'text-danger',
      bgColor: 'bg-danger-tint'
    },
    warning: {
      icon: AlertCircle,
      iconColor: 'text-warning',
      bgColor: 'bg-warning-tint'
    },
    info: {
      icon: Info,
      iconColor: 'text-info',
      bgColor: 'bg-info-tint'
    }
  };

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="sm:flex sm:items-start">
        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.bgColor} sm:mx-0 sm:h-10 sm:w-10`}>
          <Icon className={`h-6 w-6 ${config.iconColor}`} strokeWidth={1.5} />
        </div>
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3 className="text-section-title font-display text-ink">
            {title}
          </h3>
          <div className="mt-2">
            <p className="text-body text-ink-soft">
              {message}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:justify-end">
        <Button
          type="button"
          variant="primary"
          onClick={onClose}
          className="w-full sm:w-auto"
        >
          {buttonText}
        </Button>
      </div>
    </Modal>
  );
};

// Form Modal
export const FormModal = ({
  isOpen = false,
  onClose,
  onSubmit,
  title,
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  loading = false,
  size = 'md',
  submitDisabled = false
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size={size}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {children}
        </div>

        <div className="border-t border-border-default pt-4 mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || submitDisabled}
            loading={loading}
          >
            {submitText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Drawer/Slide Modal
export const DrawerModal = ({
  isOpen = false,
  onClose,
  title,
  children,
  position = 'right', // 'left', 'right', 'top', 'bottom'
  size = 'md',
  showCloseButton = true
}) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  };

  const positionClasses = {
    right: 'right-0 top-0 h-full',
    left: 'left-0 top-0 h-full',
    top: 'top-0 left-0 w-full',
    bottom: 'bottom-0 left-0 w-full'
  };

  const slideClasses = {
    right: 'translate-x-full',
    left: '-translate-x-full',
    top: '-translate-y-full',
    bottom: 'translate-y-full'
  };

  if (!isOpen) return null;

  const drawerContent = (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Background overlay */}
        <div
          className="absolute inset-0 bg-ink/40 transition-opacity duration-overlay"
          onClick={onClose}
        />

        {/* Drawer panel */}
        <div className={`fixed ${positionClasses[position]} ${position === 'top' || position === 'bottom' ? '' : sizeClasses[size]}`}>
          <div
            role="dialog"
            aria-modal="true"
            className={`h-full flex flex-col bg-surface-card border border-border-default shadow-overlay transform transition-transform duration-overlay ${isOpen ? 'translate-x-0 translate-y-0' : slideClasses[position]}`}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
                {title && (
                  <h3 className="text-section-title font-display text-ink">
                    {title}
                  </h3>
                )}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="min-w-touch min-h-touch flex items-center justify-center rounded-control text-ink-muted hover:text-ink hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 transition-colors duration-state"
                    aria-label="Close drawer"
                  >
                    <X className="w-6 h-6" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 text-body text-ink-soft">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(drawerContent, document.body);
};

// Modal Hook for easy state management
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = React.useState(initialState);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const toggleModal = () => setIsOpen(prev => !prev);

  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal
  };
};

// Modal Provider for managing multiple modals
export const ModalProvider = ({ children }) => {
  const [modals, setModals] = React.useState([]);

  const openModal = (modalComponent, props = {}) => {
    const id = Date.now().toString();
    setModals(prev => [...prev, { id, component: modalComponent, props }]);
    return id;
  };

  const closeModal = (id) => {
    setModals(prev => prev.filter(modal => modal.id !== id));
  };

  const closeAllModals = () => {
    setModals([]);
  };

  const value = {
    openModal,
    closeModal,
    closeAllModals
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {modals.map(({ id, component: ModalComponent, props }) => (
        <ModalComponent key={id} {...props} onClose={() => closeModal(id)} />
      ))}
    </ModalContext.Provider>
  );
};

const ModalContext = React.createContext();

export const useModalContext = () => {
  const context = React.useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};